import React, { useEffect, useMemo, useState } from "react";
import { useAppSelector, useAppDispatch, RootState } from "../../../../store";
import { selectQuotationDetails, setQuotationDetails } from "../quotationSlice";
import { Button, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useSearchParams } from "react-router-dom";
import { CreateQuotationStepsEnum } from "../quotationTypes";
import { useNavigationInterceptor } from "../QuotationNavigationContext";
import { isCustomerLoggedIn } from "../../../../helpers/bycrypt";
import {
  downloadBomPdfAPI,
  generateBOMPdfAPI,
  generate2BSOrderBomPdfAPI,
  updateQuotationAPI,
} from "../quotationApis";
import { BtnLoader } from "../../../../Components/Components";
import CommonFilePreviewModal from "../../../Components/CommonFilePreviewModal";
import { AttachedDrawings } from "../QuotationDetails/ProductDetails";
import { CommonConfirmationModal } from "../../../../Components/CommonConfirmationModal";
import {
  regenerateOrderBomAPI,
  regenerate2BSOrderBomAPI,
} from "../../Orders/orderApis";
import {
  GetOrderBomAndWarningsAPI,
  Get2BSOrderBomAPI,
  GetOrdersDetailsAPI,
} from "../../../Redux/orders";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../../helpers/permissionModule";
import FullScreenLoader from "../../../Components/FullScreenLoader";
import { svg } from "../../../../Assets/svg";
/**
 * Factory Assembled Separator Placement:
 * - "bottom": Standard parts first, then all Factory Assembled processes grouped at the bottom of the product (Option 1)
 * - "inline": Factory Assembled processes placed directly after each respective part (Option 2)
 */
const SEPARATOR_PLACEMENT: "bottom" | "inline" = "bottom";

const BOMDetails = ({
  data,
  selectedVariants = [],
  showNavigationButtons = false,
  isOrder: isOrderProp,
  orderId: orderIdProp,
}: {
  data?: any;
  selectedVariants?: any[];
  showNavigationButtons?: boolean;
  isOrder?: boolean;
  orderId?: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const quotationId = searchParams.get("quotationId");
  const reduxData = useAppSelector(selectQuotationDetails);
  const dispatch = useAppDispatch();
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { setInterceptor } = useNavigationInterceptor();

  const [previewFiles, setPreviewFiles] = useState<any>(null);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);

  const { getOrderBomAndWarningsLoading } = useAppSelector(
    (state: RootState) => state?.loading,
  );
  const orderPermissions = usePermissionsChecker(
    PagePermissionNameData?.ORDERS,
  );

  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  useEffect(() => {
    setInterceptor(() => async (actionType?: "back" | "tab-switch") => {
      if (actionType === "back" && quotationId) {
        return new Promise<boolean>((resolve) => {
          const payload = {
            quote_id: quotationId,
            step_no: CreateQuotationStepsEnum.BOM_DETAILS,
            last_completed_step: CreateQuotationStepsEnum.BOM_DETAILS,
          };
          dispatch(
            updateQuotationAPI({
              payload,
              onSuccess: (data) => {
                dispatch(setQuotationDetails(data));
                resolve(true);
              },
              onError: () => resolve(true),
            }),
          );
        });
      }
      return true;
    });
    return () => setInterceptor(null);
  }, [quotationId, dispatch, setInterceptor]);

  const activeData = data || reduxData;
  const bomDetails = activeData?.bom_details || [];

  const hasFactoryAssemblyBom = useMemo(() => {
    return (bomDetails || []).some(
      (item: any) => item?.is_factory_assembly_bom === true,
    );
  }, [bomDetails]);

  const isOrder =
    isOrderProp !== undefined
      ? isOrderProp
      : Boolean(
          activeData?.obj_quote_id ||
            activeData?.order_id ||
            activeData?.order_status !== undefined,
        );
  const targetOrderId =
    orderIdProp || (isOrder ? activeData?._id || activeData?.id : null);

  const handleRegenerateBom = () => {
    if (!targetOrderId) {
      message.error("Order ID not found.");
      return;
    }
    setIsRegenerating(true);
    const apiAction = activeData?.is_2bs_order
      ? regenerate2BSOrderBomAPI
      : regenerateOrderBomAPI;

    dispatch(
      apiAction({
        order_id: targetOrderId,
        onSuccess: (resData) => {
          setIsRegenerating(false);
          setIsRegenerateModalOpen(false);
          if (resData) {
            dispatch({
              type: "GET_ORDER_BOM_REGENARATE_SUCCESS",
              payload: { ...resData },
            });
          }
          if (activeData?.is_2bs_order) {
            dispatch(Get2BSOrderBomAPI({ order_id: targetOrderId }));
          } else {
            dispatch(GetOrderBomAndWarningsAPI({ order_id: targetOrderId }));
          }
          dispatch(GetOrdersDetailsAPI({ order_id: targetOrderId }));
        },
        onError: () => {
          setIsRegenerating(false);
        },
      }),
    );
  };

  const handleDownloadPDF = () => {
    const bomType = "standard_bom";

    if (activeData?.is_2bs_order) {
      const orderIdToUse = targetOrderId || activeData?._id || activeData?.id;
      if (!orderIdToUse) {
        message.error("Order ID not found for PDF generation.");
        return;
      }
      setIsPdfLoading(true);
      dispatch(
        generate2BSOrderBomPdfAPI({
          order_id: orderIdToUse,
          onSuccess: () => {
            setIsPdfLoading(false);
          },
          onError: () => {
            setIsPdfLoading(false);
          },
        }),
      );
      return;
    }

    if (selectedVariants && selectedVariants.length > 0) {
      setIsPdfLoading(true);
      const variants = selectedVariants.map((v: any) => ({
        variant_id: v.variant_id || v._id,
        qty: v.qty || 1,
      }));
      dispatch(
        downloadBomPdfAPI({
          payload: {
            variants,
            bom_type: bomType,
          },
          onSuccess: () => {
            setIsPdfLoading(false);
          },
          onError: () => {
            setIsPdfLoading(false);
          },
        }),
      );
    } else if (activeData?._id) {
      setIsPdfLoading(true);
      const isOrder = activeData?.obj_quote_id ? true : false;
      dispatch(
        generateBOMPdfAPI({
          payload: {
            quote_id: isOrder ? activeData?.obj_quote_id : activeData?._id,
            isOrder: isOrder,
            bom_type: bomType,
          },
          onSuccess: () => {
            setIsPdfLoading(false);
          },
          onError: () => {
            setIsPdfLoading(false);
          },
        }),
      );
    } else {
      message.error("No variants or quotation found for PDF generation.");
    }
  };

  const tableData = useMemo(() => {
    const allRows: any[] = [];

    const buildPartProcessRows = (
      part: any,
      partProcesses: any[],
      product: any,
      pIndex: number,
      pIdx: number,
      isAssembly = false,
    ) => {
      const partRows: any[] = [];
      const components = part.components || [];
      const renderedPartProcessIndices = new Set<number>();

      components.forEach((c: any, cIdx: number) => {
        const compClone = c.component_clone || {};
        const compId = compClone._id || c._id;
        const compName = c.component_name || compClone.name;

        const matchedPProcesses: { pProcess: any; originalIdx: number }[] = [];
        partProcesses.forEach((pProcess: any, ppIdx: number) => {
          const componentIds = pProcess.component_ids || [];
          const isMatched = componentIds.some(
            (uComp: any) => uComp._id === compId || uComp.name === compName,
          );
          if (isMatched) {
            matchedPProcesses.push({ pProcess, originalIdx: ppIdx });
          }
        });

        if (matchedPProcesses.length === 0) {
          matchedPProcesses.push({ pProcess: null, originalIdx: -1 });
        }

        const cProcesses =
          compClone.process_ids && compClone.process_ids.length > 0
            ? compClone.process_ids
            : [null];

        matchedPProcesses.forEach(({ pProcess, originalIdx }, mppIdx: number) => {
          const compRows: any[] = [];
          cProcesses.forEach((cProcess: any, cpIdx: number) => {
            const compQtyVal = Number(
              c.qty ? c.qty * (product.qty || 1) : c.total_qty || 0,
            );
            if (compQtyVal > 0 || c.qty || c.total_qty) {
              compRows.push({
                key: `${pIndex}-${pIdx}-${cIdx}-${mppIdx}-${cpIdx}${isAssembly ? "-fa" : ""}`,
                product,
                productIndex: pIndex + 1,
                part,
                isAssembly,
                partStockName:
                  pProcess?.process_id?.stock_id?.name ||
                  pProcess?.stock_id?.name ||
                  part?.part_clone?.stock_id?.name ||
                  "",
                pProcess,
                uComp: { ...compClone, name: compName },
                compStockName:
                  cProcess?.stock_id?.name ||
                  cProcess?.process_id?.stock_id?.name ||
                  compClone?.stock_id?.name ||
                  "",
                compQty: compQtyVal,
                cProcess,
              });
            }
          });

          if (compRows.length > 0) {
            if (originalIdx !== -1) {
              renderedPartProcessIndices.add(originalIdx);
            }
            compRows[0].compRowSpan = compRows.length;
            for (let i = 1; i < compRows.length; i++) {
              compRows[i].compRowSpan = 0;
            }
            compRows[0].pProcessRowSpan = compRows.length;
            for (let i = 1; i < compRows.length; i++) {
              compRows[i].pProcessRowSpan = 0;
            }
            partRows.push(...compRows);
          }
        });
      });

      partProcesses.forEach((pProcess: any, ppIdx: number) => {
        if (!renderedPartProcessIndices.has(ppIdx)) {
          partRows.push({
            key: `${pIndex}-${pIdx}-pProcess-${ppIdx}-empty${isAssembly ? "-fa" : ""}`,
            product,
            productIndex: pIndex + 1,
            part,
            isAssembly,
            partStockName:
              pProcess?.process_id?.stock_id?.name ||
              pProcess?.stock_id?.name ||
              part?.part_clone?.stock_id?.name ||
              "",
            pProcess,
            uComp: null,
            compStockName: "",
            compQty: null,
            cProcess: null,
            compRowSpan: 1,
            pProcessRowSpan: 1,
          });
        }
      });

      if (partRows.length === 0) {
        partRows.push({
          key: `${pIndex}-${pIdx}-empty-fallback${isAssembly ? "-fa" : ""}`,
          product,
          productIndex: pIndex + 1,
          part,
          isAssembly,
          partStockName: part?.part_clone?.stock_id?.name || "",
          pProcess: null,
          uComp: null,
          compStockName: "",
          compQty: null,
          cProcess: null,
          compRowSpan: 1,
          pProcessRowSpan: 1,
        });
      }

      partRows[0].partRowSpan = partRows.length;
      for (let i = 1; i < partRows.length; i++) {
        partRows[i].partRowSpan = 0;
      }

      return partRows;
    };

    (bomDetails || []).forEach((product: any, pIndex: number) => {
      // 1. Standalone Part in 2BS Order
      if (product.item_type === "part") {
        const partClone = product.part_clone || {};
        const partName =
          product.part_name ||
          product.part_id?.name ||
          partClone?.name ||
          "-";
        const partQtyVal = Number(
          product.order_qty ?? product.total_qty ?? product.qty ?? 0,
        );

        const partObj = {
          ...product,
          part_name: partName,
          total_qty: partQtyVal,
          qty: partQtyVal,
          part_clone: partClone,
        };

        const stdProcesses = partClone.part_processes || [];
        const stdRows = buildPartProcessRows(
          partObj,
          stdProcesses,
          product,
          pIndex,
          0,
          false,
        );

        const faProcesses = partClone.part_assembly_processes || [];
        const faRows =
          faProcesses.length > 0
            ? buildPartProcessRows(partObj, faProcesses, product, pIndex, 0, true)
            : [];

        let standalonePartRows = [...stdRows];
        if (faRows.length > 0) {
          standalonePartRows.push({
            key: `${pIndex}-fa-sep`,
            isSeparator: true,
            separatorTitle: "",
            product,
            productIndex: pIndex + 1,
            part: null,
            partRowSpan: 1,
            pProcessRowSpan: 1,
            compRowSpan: 1,
          });
          standalonePartRows.push(...faRows);
        }

        if (standalonePartRows.length > 0) {
          standalonePartRows[0].productRowSpan = standalonePartRows.length;
          for (let i = 1; i < standalonePartRows.length; i++) {
            standalonePartRows[i].productRowSpan = 0;
          }
        }

        allRows.push(...standalonePartRows);
        return;
      }

      // 2. Standalone Component in 2BS Order
      if (product.item_type === "component") {
        const compClone = product.component_clone || {};
        const compName =
          product.component_name ||
          product.component_id?.name ||
          compClone?.name ||
          "-";
        const compQtyVal = Number(
          product.order_qty ?? product.total_qty ?? product.qty ?? 0,
        );

        const cProcesses =
          compClone.process_ids && compClone.process_ids.length > 0
            ? compClone.process_ids
            : [null];

        const compRows: any[] = [];
        cProcesses.forEach((cProcess: any, cpIdx: number) => {
          compRows.push({
            key: `${pIndex}-comp-${cpIdx}`,
            product,
            productIndex: pIndex + 1,
            part: null,
            partStockName: "",
            pProcess: null,
            uComp: { ...compClone, name: compName },
            compStockName:
              cProcess?.stock_id?.name ||
              cProcess?.process_id?.stock_id?.name ||
              compClone?.stock_id?.name ||
              "",
            compQty: compQtyVal,
            cProcess,
            compRowSpan: 1,
            pProcessRowSpan: 1,
            partRowSpan: 1,
            productRowSpan: 1,
          });
        });

        compRows[0].compRowSpan = compRows.length;
        for (let i = 1; i < compRows.length; i++) {
          compRows[i].compRowSpan = 0;
        }

        compRows[0].partRowSpan = compRows.length;
        for (let i = 1; i < compRows.length; i++) {
          compRows[i].partRowSpan = 0;
        }

        compRows[0].pProcessRowSpan = compRows.length;
        for (let i = 1; i < compRows.length; i++) {
          compRows[i].pProcessRowSpan = 0;
        }

        compRows[0].productRowSpan = compRows.length;
        for (let i = 1; i < compRows.length; i++) {
          compRows[i].productRowSpan = 0;
        }

        allRows.push(...compRows);
        return;
      }

      const parts = product.bom_part_clone_obj || [];
      let productRows: any[] = [];

      if (parts.length === 0) {
        productRows.push({
          key: `${pIndex}-empty-prod`,
          product,
          productIndex: pIndex + 1,
          part: null,
          partStockName: "",
          pProcess: null,
          uComp: null,
          compStockName: "",
          compQty: null,
          cProcess: null,
          compRowSpan: 1,
          pProcessRowSpan: 1,
          partRowSpan: 1,
          productRowSpan: 1,
        });
      } else {
        const standardPartRows: any[] = [];
        const factoryAssembledGroups: {
          part: any;
          pIdx: number;
          rows: any[];
        }[] = [];

        parts.forEach((part: any, pIdx: number) => {
          const stdProcesses = part.part_clone?.part_processes || [];
          const stdRows = buildPartProcessRows(
            part,
            stdProcesses,
            product,
            pIndex,
            pIdx,
            false,
          );

          if (part?.qty || part?.total_qty || parts?.length === 1) {
            standardPartRows.push(...stdRows);
          }

          const faProcesses = part.part_clone?.part_assembly_processes || [];
          if (faProcesses.length > 0) {
            const faRows = buildPartProcessRows(
              part,
              faProcesses,
              product,
              pIndex,
              pIdx,
              true,
            );
            if (part?.qty || part?.total_qty || parts?.length === 1) {
              factoryAssembledGroups.push({ part, pIdx, rows: faRows });
            }
          }
        });

        if (SEPARATOR_PLACEMENT === "bottom") {
          // Standard parts first, then single partition for all Factory Assembled parts
          productRows = [...standardPartRows];
          if (factoryAssembledGroups.length > 0) {
            productRows.push({
              key: `${pIndex}-fa-sep`,
              isSeparator: true,
              separatorTitle: "",
              product,
              productIndex: pIndex + 1,
              part: null,
              partRowSpan: 1,
              pProcessRowSpan: 1,
              compRowSpan: 1,
            });
            factoryAssembledGroups.forEach(({ rows }) => {
              productRows.push(...rows);
            });
          }
        } else {
          // Option 2 (Inline): Place assembly rows directly after each respective part
          parts.forEach((part: any, pIdx: number) => {
            const stdProcesses = part.part_clone?.part_processes || [];
            const stdRows = buildPartProcessRows(
              part,
              stdProcesses,
              product,
              pIndex,
              pIdx,
              false,
            );
            if (part?.qty || part?.total_qty || parts?.length === 1) {
              productRows.push(...stdRows);
            }
            const faProcesses = part.part_clone?.part_assembly_processes || [];
            if (faProcesses.length > 0) {
              const faRows = buildPartProcessRows(
                part,
                faProcesses,
                product,
                pIndex,
                pIdx,
                true,
              );
              if (part?.qty || part?.total_qty || parts?.length === 1) {
                productRows.push({
                  key: `${pIndex}-${pIdx}-fa-sep`,
                  isSeparator: true,
                  separatorTitle: `FACTORY ASSEMBLED — ${part.part_name || part.name || "Part"}`,
                  product,
                  productIndex: pIndex + 1,
                  part,
                  partRowSpan: 1,
                  pProcessRowSpan: 1,
                  compRowSpan: 1,
                });
                productRows.push(...faRows);
              }
            }
          });
        }
      }

      if (productRows.length > 0) {
        productRows[0].productRowSpan = productRows.length;
        for (let i = 1; i < productRows.length; i++) {
          productRows[i].productRowSpan = 0;
        }
      }

      allRows.push(...productRows);
    });

    const filteredRows = allRows?.filter(
      (row) =>
        row?.isSeparator ||
        !row?.part ||
        Number(row?.part?.total_qty || row?.part?.qty || 0) > 0,
    );
    return filteredRows;
  }, [bomDetails]);

  const isCustomer = isCustomerLoggedIn();

  const allColumns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      dataIndex: "index",
      width: 60,
      onHeaderCell: () => ({
        style: { backgroundColor: "#44BF6E", color: "#fff" },
      }),
      onCell: (record) => ({
        rowSpan: record.isSeparator ? 0 : record.productRowSpan,
        style: { verticalAlign: "top", backgroundColor: "#E5F7EA" },
      }),
      render: (_, record) => (record.isSeparator ? null : record?.productIndex),
    },
    {
      title: "Products",
      dataIndex: "productName",
      width: 180,
      onHeaderCell: () => ({
        style: { backgroundColor: "#44BF6E", color: "#fff" },
      }),
      onCell: (record) => ({
        rowSpan: record.isSeparator ? 0 : record.productRowSpan,
        style: {
          verticalAlign: "top",
          backgroundColor: "#E5F7EA",
          wordBreak: "break-word",
          whiteSpace: "normal",
        } as React.CSSProperties,
      }),
      render: (_, record) =>
        record.isSeparator
          ? null
          : record.product?.bom_variant_clone_obj?.variant_name || "-",
    },
    {
      title: "Qty",
      dataIndex: "productQty",
      width: 50,
      onHeaderCell: () => ({
        style: { backgroundColor: "#44BF6E", color: "#fff" },
      }),
      onCell: (record) => ({
        rowSpan: record.isSeparator ? 0 : record.productRowSpan,
        style: { verticalAlign: "top", backgroundColor: "#E5F7EA" },
      }),
      render: (_, record) => {
        if (record.isSeparator) return null;
        const isNotProduct =
          record.product?.item_type === "part" ||
          record.product?.item_type === "component";
        if (isNotProduct) {
          return "-";
        }
        return (
          <div>
            <div>{record.product?.qty || "-"}</div>
            <div className="fs-8 text-secondary mt-1">
              {record.product?.labelInQuotation || "Qty"}
            </div>
          </div>
        );
      },
    },
    {
      title: "Parts",
      dataIndex: "partName",
      width: 150,
      onHeaderCell: () => ({
        style: { backgroundColor: "#EA7E58", color: "#fff" },
      }),
      onCell: (record) => {
        if (record.isSeparator) {
          return {
            colSpan: isCustomer ? 2 : 10,
            style: {
              verticalAlign: "middle",
              textAlign: "center",
              backgroundColor: "#FFFBEB",
              borderTop: "2px solid #F59E0B",
              borderBottom: "2px solid #F59E0B",
              padding: "10px 16px",
            } as React.CSSProperties,
          };
        }
        return {
          rowSpan: record.partRowSpan,
          style: {
            verticalAlign: "top",
            backgroundColor: "#FDF0EA",
            wordBreak: "break-word",
            whiteSpace: "normal",
          } as React.CSSProperties,
        };
      },
      render: (_, record) => {
        if (record.isSeparator) {
          return (
            <div className="text-center w-100">
              <span
                className="fw-bold text-uppercase"
                style={{
                  color: "#B45309",
                  fontSize: "13px",
                  // letterSpacing: "1px",
                }}
              >
                {record.separatorTitle || "Factory Assembled"}
              </span>
            </div>
          );
        }
        return record.part?.total_qty || record.part?.qty > 0
          ? record.part?.part_name || "-"
          : "";
      },
    },
    {
      title: "Qty",
      dataIndex: "partQty",
      width: 50,
      onHeaderCell: () => ({
        style: { backgroundColor: "#EA7E58", color: "#fff" },
      }),
      onCell: (record) => {
        if (record.isSeparator) {
          return { colSpan: 0 };
        }
        return {
          rowSpan: record.partRowSpan,
          style: { verticalAlign: "top", backgroundColor: "#FDF0EA" },
        };
      },
      render: (_, record) =>
        record.isSeparator
          ? null
          : record.part?.total_qty || record.part?.qty
          ? record.part?.total_qty || record.part?.qty || "0"
          : "",
    },
    ...(!isCustomer
      ? [
          {
            title: "Process",
            dataIndex: "pProcessName",
            width: 110,
            onHeaderCell: () => ({
              style: { backgroundColor: "#448AD0", color: "#fff" },
            }),
            onCell: (record: any) => {
              if (record.isSeparator) {
                return { colSpan: 0 };
              }
              return {
                rowSpan: record.pProcessRowSpan,
                style: {
                  verticalAlign: "top",
                  backgroundColor: "#DCEDFF",
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                } as React.CSSProperties,
              };
            },
            render: (_: any, record: any) => {
              if (record.isSeparator) return null;
              return record.part?.total_qty || record.part?.qty
                ? record.pProcess?.name ||
                    record.pProcess?.process_id?.name ||
                    "-"
                : "";
            },
          },
          {
            title: "Machine",
            dataIndex: "pProcessMachine",
            width: 100,
            onHeaderCell: () => ({
              style: { backgroundColor: "#448AD0", color: "#fff" },
            }),
            onCell: (record: any) => {
              if (record.isSeparator) {
                return { colSpan: 0 };
              }
              return {
                rowSpan: record.pProcessRowSpan,
                style: {
                  verticalAlign: "top",
                  backgroundColor: "#DCEDFF",
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                } as React.CSSProperties,
              };
            },
            render: (_: any, record: any) =>
              record.isSeparator
                ? null
                : record.part?.total_qty || record.part?.qty
                ? record.pProcess?.machine_id?.name ||
                  record.pProcess?.process_id?.machine_id?.name ||
                  "-"
                : "",
          },
          {
            title: "Parts Stock",
            dataIndex: "cProcessStock",
            width: 110,
            onHeaderCell: () => ({
              style: { backgroundColor: "#696969", color: "#fff" },
            }),
            onCell: (record: any) => {
              if (record.isSeparator) {
                return { colSpan: 0 };
              }
              return {
                style: {
                  verticalAlign: "top",
                  backgroundColor: "#E3E3E3",
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                } as React.CSSProperties,
              };
            },
            render: (_: any, record: any) => {
              if (record.isSeparator || (!record.part?.total_qty && !record.part?.qty)) return "";
              return record.partStockName ? record.partStockName : "-";
            },
          },
          {
            title: "Components",
            dataIndex: "uCompName",
            width: 180,
            onHeaderCell: () => ({
              style: { backgroundColor: "#A756AC", color: "#fff" },
            }),
            onCell: (record: any) => {
              if (record.isSeparator) {
                return { colSpan: 0 };
              }
              return {
                rowSpan: record.compRowSpan,
                style: {
                  verticalAlign: "top",
                  backgroundColor: "#F6EAF6",
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                } as React.CSSProperties,
              };
            },
            render: (_: any, record: any) =>
              record.isSeparator
                ? null
                : record.compQty
                ? record.uComp?.name || "-"
                : "",
          },
          {
            title: "Qty",
            dataIndex: "uCompQty",
            width: 50,
            onHeaderCell: () => ({
              style: { backgroundColor: "#A756AC", color: "#fff" },
            }),
            onCell: (record: any) => {
              if (record.isSeparator) {
                return { colSpan: 0 };
              }
              return {
                rowSpan: record.compRowSpan,
                style: { verticalAlign: "top", backgroundColor: "#F6EAF6" },
              };
            },
            render: (_: any, record: any) =>
              record.isSeparator ? null : record.compQty || "",
          },
          {
            title: "Process",
            dataIndex: "cProcessName",
            width: 110,
            onHeaderCell: () => ({
              style: { backgroundColor: "#448AD0", color: "#fff" },
            }),
            onCell: (record: any) => {
              if (record.isSeparator) {
                return { colSpan: 0 };
              }
              return {
                style: {
                  verticalAlign: "top",
                  backgroundColor: "#DCEDFF",
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                } as React.CSSProperties,
              };
            },
            render: (_: any, record: any) =>
              record.isSeparator
                ? null
                : record.compQty
                ? record.cProcess?.name ||
                  record.cProcess?.process_id?.name ||
                  "-"
                : "",
          },
          {
            title: "Machine",
            dataIndex: "cProcessMachine",
            width: 100,
            onHeaderCell: () => ({
              style: { backgroundColor: "#448AD0", color: "#fff" },
            }),
            onCell: (record: any) => {
              if (record.isSeparator) {
                return { colSpan: 0 };
              }
              return {
                style: {
                  verticalAlign: "top",
                  backgroundColor: "#DCEDFF",
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                } as React.CSSProperties,
              };
            },
            render: (_: any, record: any) =>
              record.isSeparator
                ? null
                : record.compQty
                ? record.cProcess?.machine_id?.name ||
                  record.cProcess?.process_id?.machine_id?.name ||
                  "-"
                : "",
          },
          {
            title: "Components Stock",
            dataIndex: "cProcessStock",
            width: 110,
            onHeaderCell: () => ({
              style: { backgroundColor: "#696969", color: "#fff" },
            }),
            onCell: (record: any) => {
              if (record.isSeparator) {
                return { colSpan: 0 };
              }
              return {
                style: {
                  verticalAlign: "top",
                  backgroundColor: "#E3E3E3",
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                } as React.CSSProperties,
              };
            },
            render: (_: any, record: any) => {
              if (record.isSeparator || !record.compQty) return "";
              return record.compStockName ? record.compStockName : "-";
            },
          },
        ]
      : []),
  ];

  const columns = allColumns;

  return (
    <div className="w-full h-100">
      <div className="d-flex flex-wrap justify-content-between align-items-md-center align-items-stretch mb-md-3 mb-2 gap-2">
        <div className="d-flex flex-wrap align-items-center gap-md-3 gap-2 mob-w-100">
          <h3 className="fs-4 fw-bold text-black mb-0">BOM Details</h3>
        </div>
        {!isCustomer && (bomDetails.length !== 0 || isOrder) ? (
          <div className="d-flex flex-wrap justify-content-md-end justify-content-start align-items-center gap-2 mob-w-100">
            {isOrder && orderPermissions?.MODIFY && (
              <button
                className="btn btn-lg btn-outline-primary mob-w-100"
                disabled={isRegenerating || isPdfLoading}
                onClick={() => setIsRegenerateModalOpen(true)}
              >
                {isRegenerating ? (
                  <>
                    <BtnLoader /> Regenerating...
                  </>
                ) : (
                  "Regenerate BOM"
                )}
              </button>
            )}
            {bomDetails.length !== 0 && (
              <button
                className="btn btn-lg btn-primary d-inline-flex align-items-center justify-content-center gap-2 mob-w-100"
                disabled={isPdfLoading || isRegenerating}
                onClick={handleDownloadPDF}
              >
                {isPdfLoading ? (
                  <>
                    <BtnLoader /> Downloading...
                  </>
                ) : (
                  <>
                    <span className="svg-20 d-inline-flex align-items-center">
                      {svg?.downlodIcon}
                    </span>
                    Download BOM PDF
                  </>
                )}
              </button>
            )}
          </div>
        ) : (
          ""
        )}
      </div>
      {getOrderBomAndWarningsLoading ? (
        <div
          style={{ height: "100%" }}
          className="d-flex flex-column align-items-center justify-content-center py-4"
        >
          <FullScreenLoader />
        </div>
      ) : tableData?.length === 0 ? (
        <div className="card border-0 rounded-4 mb-4">
          <div className="card-body page-form p-30">
            <div className="text-center">
              <div className="text-center text-muted py-4">
                No BOM Details Available
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <Table
            columns={columns}
            dataSource={tableData}
            pagination={false}
            bordered
            size="small"
            className="custom-table bomDetailsTable table-scroll-ui"
            scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
          />
        </div>
      )}

      {showNavigationButtons && (
        <div className="row justify-content-center mt-4">
          <div className="col-sm-auto col-6">
            <Button
              className="btn btn-lg btn-outline-primary px-4 h-auto w-100 text-uppercase"
              onClick={() => {
                const currentId = searchParams.get("quotationId");
                const mode = searchParams.get("mode");
                const params: any = {
                  step: CreateQuotationStepsEnum.SERVICES,
                  quotationId: currentId,
                };
                if (mode) params.mode = mode;
                setSearchParams(params);
              }}
            >
              Previous
            </Button>
          </div>
          <div className="col-sm-auto col-6">
            <Button
              className="btn btn-lg btn-primary px-4 h-auto w-100 text-uppercase"
              onClick={() => {
                if (!quotationId) return;
                setSaving(true);
                const payload = {
                  quote_id: quotationId,
                  step_no: CreateQuotationStepsEnum.BOM_DETAILS,
                  last_completed_step: CreateQuotationStepsEnum.BOM_DETAILS,
                };

                dispatch(
                  updateQuotationAPI({
                    payload,
                    onSuccess: (data) => {
                      dispatch(setQuotationDetails(data));
                      const mode = searchParams.get("mode");
                      const nextParams: any = {
                        step: CreateQuotationStepsEnum.LOAD_NOTICES,
                        quotationId: quotationId,
                      };
                      if (mode === "edit") nextParams.mode = "edit";
                      setSearchParams(nextParams);
                      setSaving(false);
                    },
                    onError: () => {
                      setSaving(false);
                    },
                  }),
                );
              }}
              loading={saving}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      {previewModalOpen && (
        <CommonFilePreviewModal
          visible={previewModalOpen}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewFiles([]);
            setPreviewIndex(0);
          }}
          files={previewFiles}
          activeIndex={previewIndex}
          onActiveIndexChange={setPreviewIndex}
        />
      )}
      <CommonConfirmationModal
        open={isRegenerateModalOpen}
        onClose={() => setIsRegenerateModalOpen(false)}
        onSuccess={handleRegenerateBom}
        title="Regenerate Order BOM, Are You Sure?"
        subTitle="This will recalculate and regenerate BOM details for this order using the latest master data."
        confirmButtonColorClass="btn-primary"
        confirmButtonText="Regenerate"
        loading={isRegenerating}
      />
    </div>
  );
};

export default BOMDetails;
